import { Chip, Tooltip, Typography } from '@mui/material';
import { formatFlexworkerCount } from 'shared/utils/formatting';
import { Column } from 'react-table';
import { LevelTableCellProps, TableCell, TableHeader } from 'redesign';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetStatistics } from 'ogp/hooks/queryHooks/statistics/use-get-statistics';
import { ExternalFlexPoolsTableColumnDefinition } from './external-flex-pools-table.types';

const SERVICE_AGREEMENTS_MAX_DISPLAY_COUNT = 4;

export const useExternalFlexPoolsColumnDefinition =
  (): Column<ExternalFlexPoolsTableColumnDefinition>[] => {
    const { t } = useTranslation('ogp-screen-specific');
    const { data: statisticsData } = useGetStatistics('workers-pools');
    const workersPoolsStatistic = statisticsData?.find((stat) => stat.label === 'workersPools');
    const serviceAgreementsStatistic = statisticsData?.find(
      (stat) => stat.label === 'serviceAgreements'
    );

    return useMemo(() => {
      const flexpool: Column<ExternalFlexPoolsTableColumnDefinition> = {
        accessor: 'flexPoolName',
        Header: () => (
          <TableHeader>
            {t('flexpools.table_headers.flexpool', { ns: 'ogp-screen-specific' })}
            {workersPoolsStatistic != null ? ` (${workersPoolsStatistic.value})` : ''}
          </TableHeader>
        ),
        Cell: ({ value }) => <TableCell>{value}</TableCell>,
      };

      const serviceAgreements: Column<ExternalFlexPoolsTableColumnDefinition> = {
        accessor: 'serviceAgreementNames',
        Header: () => (
          <TableHeader>
            {t('flexpools.table_headers.service_agreements', { ns: 'ogp-screen-specific' })}
            {serviceAgreementsStatistic != null ? ` (${serviceAgreementsStatistic.value})` : ''}
          </TableHeader>
        ),
        Cell: ServiceAgreementsCell,
        width: 600,
      };

      const activeWorkersCount: Column<ExternalFlexPoolsTableColumnDefinition> = {
        accessor: 'activeWorkersCount',
        Header: () => (
          <TableHeader>
            {t('flexpools.table_headers.flexworkers', { ns: 'ogp-screen-specific' })}
          </TableHeader>
        ),
        Cell: ({ value }) => <TableCell>{formatFlexworkerCount(value)}</TableCell>,
      };

      return [flexpool, serviceAgreements, activeWorkersCount];
    }, [t, workersPoolsStatistic, serviceAgreementsStatistic]);
  };

const ServiceAgreementsCell = ({
  value,
}: LevelTableCellProps<
  ExternalFlexPoolsTableColumnDefinition,
  ExternalFlexPoolsTableColumnDefinition['serviceAgreementNames']
>) => {
  const serviceAgreementsCount = value.length;
  const extraServiceAgreements = value.slice(SERVICE_AGREEMENTS_MAX_DISPLAY_COUNT).join(', ');

  return (
    <TableCell style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
      {value.slice(0, SERVICE_AGREEMENTS_MAX_DISPLAY_COUNT).map((name, index) => (
        <Chip key={index} label={name} style={{ marginBottom: 0 }} />
      ))}
      {serviceAgreementsCount > SERVICE_AGREEMENTS_MAX_DISPLAY_COUNT && (
        <Tooltip title={extraServiceAgreements}>
          <Typography variant="body2">{`+${serviceAgreementsCount - SERVICE_AGREEMENTS_MAX_DISPLAY_COUNT}`}</Typography>
        </Tooltip>
      )}
    </TableCell>
  );
};
